// ============================================================================
// ChatPanel — Chat message area + input
// ============================================================================

import { useState, useRef, useEffect } from 'react'
import type { ReactNode } from 'react'
import { Box, TextField, IconButton, Alert, Typography } from '@mui/material'
import { ArrowUpward, Stop, Close, AttachFile, PhotoCamera, Mic, GraphicEq } from '@mui/icons-material'
import { beoflowColors, spacing, borderRadius, layout, opacity } from '@/theme/muiTheme'
import { useThemeMode } from '@/contexts/ThemeContext'
import { useAudioRecorder } from '@/hooks/useAudioRecorder'
import {
  ATTACHMENT_ACCEPT,
  ACCEPTED_ATTACHMENT_TYPES,
  ATTACHMENT_MAX_PDF_BYTES,
  ATTACHMENT_MAX_IMAGE_BYTES,
  ATTACHMENT_MAX_TEXT_BYTES,
  MAX_ATTACHMENTS,
  type ChatAttachment,
} from '@/services/api/chat'
import MessageBubble from './MessageBubble'
import AttachmentChip from './AttachmentChip'
import FollowupChips from './cards/FollowupChips'
import LongTurnBanner from './LongTurnBanner'
import type { ChatMessage } from '@/hooks/useChat'

interface ChatPanelProps {
  messages: ChatMessage[]
  isStreaming: boolean
  error: string | null
  onSend: (message: string, attachments?: ChatAttachment[], audio?: string) => void
  onStop: () => void
  onDismissError: () => void
  /** Re-send the last user message, replacing the last assistant turn. */
  onRegenerate?: () => void
  suggestedPrompts?: string[]
  /** The empty thread's headline. */
  emptyTitle?: string
  placeholder?: string
  /** Enable the file-attachment picker, camera capture, and voice dictation. */
  enableAttachments?: boolean
  /** Slot rendered at the top of the composer (e.g. the "Asking about" selector). */
  composerHeader?: ReactNode
  /** Force the docked layout (composer at the bottom, no centered greeting) even
   *  when empty — used when the chat is the lower half of a split with a view. */
  dockComposer?: boolean
  /** Text the composer opens with, for the user to edit before sending. */
  initialInput?: string
}

/** Resolve a normalized media type from a File (browsers sometimes leave
 *  `type` empty; fall back to the extension for csv/txt/xlsx/docx). Workout
 *  files go by extension first: browsers label them `application/octet-stream`,
 *  `application/xml` or nothing. A .zwo is XML Flow reads as text. */
function resolveMediaType(file: File): string {
  const ext = file.name.split('.').pop()?.toLowerCase()
  if (ext === 'fit') return FIT_MEDIA_TYPE
  if (ext === 'zwo') return 'text/plain'
  if (file.type) return file.type === 'image/jpg' ? 'image/jpeg' : file.type
  if (ext === 'csv') return 'text/csv'
  if (ext === 'txt') return 'text/plain'
  if (ext === 'xlsx') {
    return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  }
  if (ext === 'docx') return DOCX_MEDIA_TYPE
  if (ext === 'pdf') return 'application/pdf'
  return ''
}

const XLSX_MEDIA_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
const DOCX_MEDIA_TYPE =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
const FIT_MEDIA_TYPE = 'application/vnd.ant.fit'

/** Client-side size validation mirroring the backend limits. Returns an
 *  error string, or null when the file is acceptable. */
function validateAttachment(mediaType: string, size: number): string | null {
  if (!(ACCEPTED_ATTACHMENT_TYPES as readonly string[]).includes(mediaType)) {
    return 'Unsupported file type. Use PDF, image, CSV, text, Excel, Word, or a .FIT or .ZWO workout.'
  }
  if (mediaType === 'application/pdf' && size > ATTACHMENT_MAX_PDF_BYTES) {
    return 'PDF is too large (max 25 MB).'
  }
  if (mediaType.startsWith('image/') && size > ATTACHMENT_MAX_IMAGE_BYTES) {
    return 'Image is too large (max 10 MB).'
  }
  if (
    (mediaType === 'text/csv' || mediaType === 'text/plain' || mediaType === XLSX_MEDIA_TYPE || mediaType === DOCX_MEDIA_TYPE || mediaType === FIT_MEDIA_TYPE)
    && size > ATTACHMENT_MAX_TEXT_BYTES
  ) {
    return 'File is too large (max 5 MB).'
  }
  return null
}

/** Read a File into a base64 data URI via FileReader (pattern cribbed from
 *  ExerciseMediaUpload). */
function readFileAsDataUri(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'))
    reader.readAsDataURL(file)
  })
}

const DEFAULT_PROMPTS = [
  'Am I ready to train hard today?',
  'Suggest a good workout for today',
  'Explain my recovery score today',
  'What can I improve on?',
]

export default function ChatPanel({
  messages,
  isStreaming,
  error,
  onSend,
  onStop,
  onDismissError,
  onRegenerate,
  suggestedPrompts,
  emptyTitle = 'How can Flow help today?',
  placeholder = 'Type something...',
  enableAttachments = false,
  composerHeader,
  dockComposer = false,
  initialInput = '',
}: ChatPanelProps) {
  const [input, setInput] = useState(initialInput)
  const [attachments, setAttachments] = useState<ChatAttachment[]>([])
  const [attachmentError, setAttachmentError] = useState<string | null>(null)
  // A recorded voice clip (base64 data URI) staged for the next send.
  const [pendingAudio, setPendingAudio] = useState<string | null>(null)
  const recorder = useAudioRecorder()
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const cameraInputRef = useRef<HTMLInputElement>(null)

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Focus input on mount and after sending
  useEffect(() => {
    if (!isStreaming) {
      inputRef.current?.focus()
    }
  }, [isStreaming])

  const handleSend = () => {
    const trimmed = input.trim()
    if ((!trimmed && attachments.length === 0 && !pendingAudio) || isStreaming) return
    onSend(trimmed, attachments.length ? attachments : undefined, pendingAudio ?? undefined)
    setInput('')
    setAttachments([])
    setPendingAudio(null)
    setAttachmentError(null)
  }

  // Mic toggle: start a recording, or stop and stage the clip for send.
  const handleMicClick = async () => {
    if (recorder.isRecording) {
      const uri = await recorder.stop()
      if (uri) setPendingAudio(uri)
    } else {
      setPendingAudio(null)
      recorder.clearError()
      await recorder.start()
    }
  }

  const handleFilesSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    // Allow re-selecting the same file later.
    e.target.value = ''
    if (!files.length) return
    setAttachmentError(null)

    const accepted: ChatAttachment[] = []
    for (const file of files) {
      if (attachments.length + accepted.length >= MAX_ATTACHMENTS) {
        setAttachmentError(`You can attach at most ${MAX_ATTACHMENTS} files.`)
        break
      }
      const mediaType = resolveMediaType(file)
      const validationError = validateAttachment(mediaType, file.size)
      if (validationError) {
        setAttachmentError(validationError)
        continue
      }
      try {
        const dataUri = await readFileAsDataUri(file)
        accepted.push({ filename: file.name, media_type: mediaType, data_base64: dataUri })
      } catch {
        setAttachmentError(`Could not read "${file.name}".`)
      }
    }
    if (accepted.length) {
      setAttachments(prev => [...prev, ...accepted])
    }
  }

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index))
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const isEmpty = messages.length === 0
  const { mode } = useThemeMode()
  const isLight = mode === 'light'

  const prompts = suggestedPrompts ?? DEFAULT_PROMPTS
  const canSend = input.trim().length > 0 || attachments.length > 0 || !!pendingAudio
  // The trailing control is the mic when the composer is empty (nothing to
  // send yet) and voice is available — otherwise it's the send/stop button.
  const showMic = enableAttachments && recorder.isSupported && !isStreaming && !canSend

  // Follow-up chips: only after the last assistant message, gone while
  // streaming, and they vanish as soon as the user starts typing.
  const lastMessage = messages[messages.length - 1]
  const followups =
    !isStreaming &&
    input === '' &&
    lastMessage?.role === 'assistant' &&
    lastMessage.followups?.length
      ? lastMessage.followups
      : null

  // Composer (attachment errors + chips + input bar). Rendered centered in the
  // empty state and docked at the bottom once the conversation has messages.
  const composerBar = (
    <Box
      sx={{
        px: spacing.lg,
        pt: spacing.xs,
        pb: spacing.sm,
        flexShrink: 0,
        width: '100%',
      }}
    >
      {composerHeader && (
        <Box sx={{ mb: spacing.xs }}>{composerHeader}</Box>
      )}

      {enableAttachments && (attachmentError || recorder.error) && (
        <Typography
          variant="caption"
          sx={{ color: beoflowColors.redBase, mb: spacing.xs, display: 'block' }}
        >
          {attachmentError || recorder.error}
        </Typography>
      )}

      {enableAttachments && (attachments.length > 0 || pendingAudio) && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: spacing.xs, mb: spacing.sm }}>
          {attachments.map((att, i) => (
            <AttachmentChip
              key={`${att.filename}-${i}`}
              filename={att.filename}
              mediaType={att.media_type}
              onRemove={() => removeAttachment(i)}
            />
          ))}
          {pendingAudio && (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: spacing.xs,
                px: spacing.sm,
                py: '4px',
                borderRadius: borderRadius.round,
                backgroundColor: beoflowColors.meanBlue,
              }}
            >
              <GraphicEq sx={{ fontSize: 16, color: beoflowColors.beoWhite }} />
              <Typography variant="caption" sx={{ color: beoflowColors.beoWhite }}>
                Voice message
              </Typography>
              <IconButton
                size="small"
                aria-label="Remove voice message"
                onClick={() => setPendingAudio(null)}
                sx={{ color: beoflowColors.beoWhite, p: 0 }}
              >
                <Close sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          )}
        </Box>
      )}

      <Box
        sx={{
          backgroundColor: isLight
            ? (isStreaming ? '#F0F0F0' : '#FFF')
            : 'rgba(248, 246, 244, 0.08)',
          border: isLight ? '1px solid #E0E0E0' : '1px solid rgba(248, 246, 244, 0.15)',
          borderRadius: '22px',
          backdropFilter: isLight ? 'none' : 'blur(12px)',
          WebkitBackdropFilter: isLight ? 'none' : 'blur(12px)',
          display: 'flex',
          alignItems: 'center',
          gap: spacing.md,
          p: layout.chatInputPadding,
          transition: 'background-color 150ms ease',
        }}
      >
        {enableAttachments && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ATTACHMENT_ACCEPT}
              onChange={handleFilesSelected}
              style={{ display: 'none' }}
            />
            {/* Separate input with `capture` so mobile jumps straight to the
                camera; on desktop it falls back to the file dialog. */}
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFilesSelected}
              style={{ display: 'none' }}
            />
            <IconButton
              onClick={() => fileInputRef.current?.click()}
              disabled={isStreaming || recorder.isRecording || attachments.length >= MAX_ATTACHMENTS}
              aria-label="Attach file"
              sx={{
                color: 'text.secondary',
                width: layout.chatSendButtonSize,
                height: layout.chatSendButtonSize,
                flexShrink: 0,
              }}
            >
              <AttachFile sx={{ fontSize: 20 }} />
            </IconButton>
            <IconButton
              onClick={() => cameraInputRef.current?.click()}
              disabled={isStreaming || recorder.isRecording || attachments.length >= MAX_ATTACHMENTS}
              aria-label="Take photo"
              sx={{
                color: 'text.secondary',
                width: layout.chatSendButtonSize,
                height: layout.chatSendButtonSize,
                flexShrink: 0,
              }}
            >
              <PhotoCamera sx={{ fontSize: 20 }} />
            </IconButton>
          </>
        )}
        <TextField
          inputRef={inputRef}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={isStreaming ? 'Thinking...' : recorder.isRecording ? 'Recording…' : placeholder}
          multiline
          maxRows={4}
          disabled={isStreaming || recorder.isRecording}
          fullWidth
          variant="outlined"
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              fontSize: layout.inputFontSize,
              textTransform: 'none',
              padding: 0,
              '& fieldset': { border: 'none' },
              '&:hover fieldset': { border: 'none' },
              '&.Mui-focused fieldset': { border: 'none' },
              backgroundColor: 'transparent',
              backdropFilter: 'none',
            },
            '& .MuiOutlinedInput-input': {
              padding: 0,
              py: spacing.xs,
              // Force "Thinking..." placeholder to render in beoBlack
              // instead of the default faded text.secondary while disabled.
              ...(isStreaming && {
                '&::placeholder': {
                  color: beoflowColors.beoBlack,
                  opacity: 1,
                },
                '&.Mui-disabled::placeholder': {
                  color: beoflowColors.beoBlack,
                  opacity: 1,
                  WebkitTextFillColor: beoflowColors.beoBlack,
                },
              }),
            },
          }}
        />
        {/* Trailing control: Stop while streaming, Mic when the composer is
            empty (record a clip → backend transcribes it), else Send. */}
        {isStreaming ? (
          <IconButton
            onClick={onStop}
            sx={{
              backgroundColor: beoflowColors.redBase,
              color: beoflowColors.beoWhite,
              width: layout.chatSendButtonSize,
              height: layout.chatSendButtonSize,
              borderRadius: borderRadius.round,
              flexShrink: 0,
              '&:hover': {
                backgroundColor: beoflowColors.redDark,
              },
            }}
          >
            <Stop sx={{ fontSize: 20 }} />
          </IconButton>
        ) : showMic ? (
          <IconButton
            onClick={handleMicClick}
            aria-label={recorder.isRecording ? 'Stop recording' : 'Record voice message'}
            sx={{
              color: recorder.isRecording ? beoflowColors.beoWhite : 'text.secondary',
              backgroundColor: recorder.isRecording ? beoflowColors.redBase : 'transparent',
              width: layout.chatSendButtonSize,
              height: layout.chatSendButtonSize,
              borderRadius: borderRadius.round,
              flexShrink: 0,
              '&:hover': {
                backgroundColor: recorder.isRecording ? beoflowColors.redDark : `${beoflowColors.beoWhite}${opacity.glass}`,
              },
            }}
          >
            <Mic sx={{ fontSize: 20 }} />
          </IconButton>
        ) : (
          <IconButton
            onClick={handleSend}
            disabled={!canSend}
            sx={{
              backgroundColor: canSend
                ? beoflowColors.meanBlue
                : isLight ? '#E0E0E0' : beoflowColors.beoWhite,
              color: canSend
                ? '#FFF'
                : beoflowColors.graphGreyDark,
              width: layout.chatSendButtonSize,
              height: layout.chatSendButtonSize,
              borderRadius: borderRadius.round,
              flexShrink: 0,
              '&:hover': {
                backgroundColor: canSend
                  ? beoflowColors.blueDark
                  : `${beoflowColors.beoWhite}${opacity.glass}`,
              },
              '&.Mui-disabled': {
                backgroundColor: isLight ? '#E0E0E0' : beoflowColors.beoWhite,
                color: beoflowColors.graphGreyDark,
              },
            }}
          >
            <ArrowUpward sx={{ fontSize: 20 }} />
          </IconButton>
        )}
      </Box>
    </Box>
  )

  // Suggestion chips (empty state only): a centered row of content-sized pills
  // that wrap, rather than full-width stacked rows.
  const suggestions = (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: spacing.sm,
        width: '100%',
        maxWidth: '680px',
      }}
    >
      {prompts.map((prompt) => (
        <Box
          key={prompt}
          onClick={() => onSend(prompt)}
          sx={{
            backgroundColor: isLight ? '#FFF' : 'rgba(248, 246, 244, 0.05)',
            border: isLight ? '1px solid #E0E0E0' : '1px solid rgba(248, 246, 244, 0.15)',
            borderRadius: borderRadius.round,
            px: spacing.md,
            py: spacing.xs,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            transition: 'background-color 0.2s ease',
            '&:hover': {
              backgroundColor: isLight ? '#F5F5F5' : 'rgba(248, 246, 244, 0.12)',
            },
          }}
        >
          <Typography variant="body2">{prompt}</Typography>
        </Box>
      ))}
    </Box>
  )

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
      }}
    >
      {/* Error Banner */}
      {error && (
        <Alert
          severity="error"
          action={
            <IconButton size="small" onClick={onDismissError}>
              <Close sx={{ fontSize: 16 }} />
            </IconButton>
          }
          sx={{ mx: spacing.md, mt: spacing.sm, flexShrink: 0 }}
        >
          {error}
        </Alert>
      )}

      {isEmpty && !dockComposer ? (
        /* Empty state: centered column — title above, composer in the middle,
           suggestion chips below (ChatGPT-style), not a bottom-docked input. */
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: spacing.lg,
            px: spacing.lg,
          }}
        >
          <Typography variant="h3" sx={{ textAlign: 'center' }}>
            {emptyTitle}
          </Typography>
          <Box sx={{ width: '100%', maxWidth: '680px' }}>{composerBar}</Box>
          {suggestions}
        </Box>
      ) : (
        /* Conversation: scrolling messages with the composer docked at the bottom. */
        <>
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              // Containing block for absolutely positioned card content (the
              // visually-hidden spans): without it they escape this scroller's
              // clip, stretch the fixed launcher panel's scroll area, and the
              // end-of-list scrollIntoView below scrolls the panel itself.
              position: 'relative',
              py: spacing.md,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {messages.map((msg, idx) => (
              <MessageBubble
                key={msg.id}
                message={msg}
                isLast={idx === messages.length - 1}
                onRegenerate={onRegenerate}
              />
            ))}
            {followups && <FollowupChips followups={followups} onSelect={text => onSend(text)} />}
            <div ref={messagesEndRef} />
          </Box>
          <LongTurnBanner messages={messages} isStreaming={isStreaming} />
          {composerBar}
        </>
      )}
    </Box>
  )
}
